/**
 * Hierarchical FX query keys. All provider-dependent queries use ["fx", applicationId, ...]
 * so that invalidateQueries({ queryKey: ["fx", applicationId] }) invalidates all children.
 */
export const fxQueryKeys = {
  /** Base for app-scoped invalidation; invalidating this invalidates all FX data for the app */
  all: (applicationId: string) => ["fx", applicationId] as const,

  // Config (admin + merchant-facing)
  adminConfig: (applicationId: string) =>
    ["fx", applicationId, "admin-config"] as const,
  config: (applicationId: string) => ["fx", applicationId, "config"] as const,
  eligibility: (applicationId: string) =>
    ["fx", applicationId, "fx-eligibility"] as const,

  // Merchant notification settings
  notificationConfig: (applicationId: string) =>
    ["fx", applicationId, "notification-config"] as const,
  rateAlertConfig: (applicationId: string) =>
    ["fx", applicationId, "rate-alert-config"] as const,

  // Admin: wallets and available currencies
  adminWallets: (
    applicationId: string,
    includeDisabled?: boolean,
    includeDeleted?: boolean,
  ) =>
    [
      "fx",
      applicationId,
      "admin-wallets",
      includeDisabled ?? false,
      includeDeleted ?? false,
    ] as const,
  /** Prefix for invalidating all admin wallet queries for an app */
  adminWalletsAll: (applicationId: string) =>
    ["fx", applicationId, "admin-wallets"] as const,
  adminAvailableCurrencies: (applicationId: string) =>
    ["fx", applicationId, "admin-available-currencies"] as const,
  adminProviderConfig: (provider: string) =>
    ["fx", "admin-provider-config", provider] as const,
  adminCorridorMarkupAll: () => ["fx", "admin", "corridor-markup"] as const,
  adminCorridorMarkupOne: (currencyFrom: string, currencyTo: string) =>
    [
      "fx",
      "admin",
      "corridor-markup",
      currencyFrom.toUpperCase(),
      currencyTo.toUpperCase(),
    ] as const,
  adminApplicationCorridorMarkupAll: (applicationId: string) =>
    ["fx", applicationId, "admin-application-corridor-markup"] as const,
  adminApplicationCorridorMarkupHistory: (
    applicationId: string,
    currencyFrom: string,
    currencyTo: string,
  ) =>
    [
      "fx",
      applicationId,
      "admin-application-corridor-markup",
      "history",
      currencyFrom.toUpperCase(),
      currencyTo.toUpperCase(),
    ] as const,

  // FX beneficiaries (admin)
  beneficiaries: (merchantId: string, applicationId: string) =>
    ["fx", applicationId, "beneficiaries", merchantId] as const,
  beneficiaryPurposeCodes: (applicationId: string) =>
    ["fx", applicationId, "beneficiary-purpose-codes"] as const,

  // Main app: wallet balances and beneficiaries
  walletBalances: (applicationId: string, isFxEnabled: boolean) =>
    ["fx", applicationId, "wallet-balances", isFxEnabled] as const,
  /** Prefix for invalidating all wallet balance queries for an app */
  walletBalancesAll: (applicationId: string) =>
    ["fx", applicationId, "wallet-balances"] as const,
  walletFundingDetails: (applicationId: string, internalWalletId: string) =>
    ["fx", applicationId, "wallet-funding-details", internalWalletId] as const,
  walletBeneficiaries: (applicationId: string, currency: string) =>
    ["fx", applicationId, "wallet-beneficiaries", currency] as const,

  // Provider sub accounts
  providerSubAccounts: (applicationId: string) =>
    ["fx", applicationId, "provider-sub-accounts"] as const,
  providerSubAccountOnboardingStatus: (
    applicationId: string,
    providerSubAccountId: string,
  ) =>
    [
      "fx",
      applicationId,
      "provider-sub-account-onboarding",
      providerSubAccountId,
    ] as const,
  providerSubAccountByApplicationId: (
    applicationId: string,
    provider: string,
  ) =>
    [
      "fx",
      applicationId,
      "provider-sub-account-by-application-id",
      provider,
    ] as const,

  // Rates
  ratesList: (
    applicationId: string,
    filters: readonly { source: string; destination: string }[],
    isFxEnabled: boolean,
  ) => ["fx", applicationId, "rates", "list", filters, isFxEnabled] as const,
  ratesConfig: (applicationId: string | undefined) =>
    ["fx", applicationId ?? "", "rates", "config"] as const,
} as const;
